import { ProcessedSearchResultItem } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import {
  SimilarDesignInventoryItem,
  SimilarDesignItem,
} from '@app/data/clients/rbServerSideClient/queries/GetSimilarDesigns/types';
import { getSdk, Shop_Inventory_Unit, Shop_Preview } from '@app/data/shop-bff';
import { URLS } from '@app/pages/urls';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@redbubble/next-i18n';
import { GraphQLClient } from 'graphql-request';
import { round } from 'lodash';
import getConfig from 'next/config';
import { InventoryItem } from 'types/shop-bff/types';
import { SHOP_PROXY_PUBLIC_ENDPOINT } from '../pages/i/constants';

const { publicRuntimeConfig } = getConfig();
const STATIC_ASSETS_PREFIX = publicRuntimeConfig.app.assets.prefix;

const shopBffStaticClient = new GraphQLClient(SHOP_PROXY_PUBLIC_ENDPOINT);
export const publicShopBffSdk = getSdk(shopBffStaticClient);

export const getCustomPublicShopBffSdk = (endpoint: string) => {
  const client = new GraphQLClient(`/fep/api/shop-bff/${endpoint}`);

  return getSdk(client);
};

export const isPromiseFulfilled = <T extends unknown>(
  result: PromiseSettledResult<T>
): result is PromiseFulfilledResult<T> => {
  return result.status === 'fulfilled';
};

export const getFulfilledValue = <
  T extends Record<string, any>,
  K extends T extends null ? never : keyof T,
>(
  result: PromiseFulfilledResult<T | undefined | null>,
  accessor: K
): Exclude<T[K], undefined> | null => {
  if (accessor === undefined) return null;

  const fulfilledValue = result.value ? result.value[accessor] : null;

  return fulfilledValue ?? null;
};

export const processSettledResult = <
  T extends Record<string, any>,
  K extends T extends null ? never : keyof T,
>(
  result: PromiseSettledResult<T | undefined | null>,
  processRejectedResult: (result: PromiseRejectedResult) => void,
  accessor: K
) => {
  if (!isPromiseFulfilled(result)) {
    processRejectedResult(result);
    return null;
  }

  return getFulfilledValue(result, accessor);
};

export const processSettledRESTResult = <T extends Record<string, any>>(
  result: PromiseSettledResult<T | undefined | null>,
  processRejectedResult: (result: PromiseRejectedResult) => void
) => {
  if (!isPromiseFulfilled(result)) {
    processRejectedResult(result);
    return null;
  }

  return result.value ? result.value : null;
};

export const getUnit = (currency: string) => {
  return currency.toUpperCase() === 'USD'
    ? Shop_Inventory_Unit.In
    : Shop_Inventory_Unit.Cm;
};

interface PreviewSet {
  previewSet?: {
    previews: Array<Omit<Shop_Preview, '__typename'>> | null;
  } | null;
}

export const getPreviewUrl = (
  item: PreviewSet | null | undefined,
  preferredPreviewTypeId: string
) => {
  if (!item || !item.previewSet) return null;

  const { previews } = item.previewSet;

  if (!previews?.length) {
    return null;
  }

  const preferredPreview = previews.find(
    ({ previewTypeId }) => previewTypeId === preferredPreviewTypeId
  );
  if (preferredPreview?.url) {
    return preferredPreview.url;
  }

  return previews[0].url;
};

export const getDiscountValue = (
  price:
    | InventoryItem['price']
    | Omit<NonNullable<InventoryItem['price']>, 'id'>
    | ProcessedSearchResultItem['price']
    | NonNullable<SimilarDesignItem['inventoryItem']>['price']
): number => {
  if (price && price.discount && price.discount.percent > 0) {
    return round(price.amount - price.discount.amount, 2);
  }
  return 0;
};

/**
 * the hostname of the current window location
 * only to be used in the client
 */
export const getCurrentHostName = (): string => {
  const { hostname } = new URL(window.location.origin);
  return hostname;
};

/**
 * returns the hostname of the referrer if it exists and is not the same as the current hostname
 * otherwise returns an empty string
 * only to be used in the client
 */
export const getExternalReferrer = (): string => {
  if (document && document.referrer) {
    const referrer = new URL(document.referrer);

    return referrer.hostname === getCurrentHostName() ? '' : referrer.hostname;
  }
  return '';
};

export const localizedPath = (locale: string, path: string): string => {
  if (!SUPPORTED_LOCALES.includes(locale) || locale === DEFAULT_LOCALE) {
    return path;
  }

  return `/${locale}${path}`;
};

/**
 * Returns either the discount amount if it exists or the price amount
 */
export const getItemValue = (
  price:
    | NonNullable<InventoryItem['price']>
    | NonNullable<ProcessedSearchResultItem['price']>
    | NonNullable<SimilarDesignInventoryItem['price']>
): string => {
  if (price.discount && price.discount.percent > 0)
    return String(price.discount.amount);

  return String(price.amount);
};

export const getImageUrl = (path: string) => {
  return STATIC_ASSETS_PREFIX ? `${STATIC_ASSETS_PREFIX}${path}` : path;
};

export const constructShopTagSlug = (tag: string, suffix: string): string => {
  return `${URLS.SEARCH_CONTEXT_LANDING_PAGE}/${[
    tag.toLowerCase().split(/\s+/).join('+'),
    suffix.toLowerCase().split(/\s+/).join('-'),
  ]
    .filter(Boolean)
    .join('+')}`;
};

export const getResultOrEmptyArray = <T>(
  result: PromiseSettledResult<T>
): T | [] => {
  if (!isPromiseFulfilled(result)) {
    return [];
  }
  return result.value;
};

export const getResultOrNull = <T>(
  result: PromiseSettledResult<T>
): T | null => {
  if (!isPromiseFulfilled(result)) {
    return null;
  }
  return result.value;
};
